import { usePathname, useRouter } from 'next/navigation'
import { createContext, useContext, useEffect, useState } from 'react'

import { ConnectorFn, WALLET_CONNECTORS, Web3AuthNoModal, Web3AuthOptions, authConnector } from '@web3auth/modal'

import { needsMfa } from '@infura/packages.common/user'

import { api } from 'lib/api/api'
import {
  getAuthConnection,
  getErrorMessage,
  getRedirecURL,
  getWeb3AuthErrorMessage,
  isValidWeb3AuthNetwork
} from 'lib/web3auth-utils'

const clientId = process.env.NEXT_PUBLIC_WEB3AUTH_CLIENT_ID
const network = process.env.NEXT_PUBLIC_WEB3AUTH_NETWORK

if (!clientId) throw new Error('NEXT_PUBLIC_WEB3AUTH_CLIENT_ID is not set')
if (!network) throw new Error('NEXT_PUBLIC_WEB3AUTH_NETWORK is not set')
if (!isValidWeb3AuthNetwork(network)) throw new Error('NEXT_PUBLIC_WEB3AUTH_NETWORK is not valid')

export type LoginProvider =
  | 'google'
  | 'github'
  | 'twitter'
  | 'facebook'
  | 'discord'
  | 'line'
  | 'reddit'
  | 'apple'
  | 'twitch'
  | 'linkedin'
  | 'wechat'
  | 'kakao'

interface IWeb3Context {
  error: string | null
  submitting: boolean
  ready: boolean
  loggedIn: boolean
  loginProvider: LoginProvider | null
  authenticate: (provider: LoginProvider) => void
  logout: () => void
}

interface Web3ProviderProps {
  children: React.ReactNode
  csrfToken?: string
}

const Web3Contenxt = createContext<IWeb3Context>(null!)

const initWeb3Auth = async (csrfToken?: string) => {
  // Web3Auth doesn't support wildcard when whitelisting URLs and Vercel always creates a new URL for each preview deployment
  // so we need to sign the origin URL for each preview environment using generateWeb3AuthSignature and pass the signature to Web3Auth
  const isVercel = window.location.hostname.endsWith('.vercel.app')
  const connectors: Array<ConnectorFn> = []
  if (isVercel) {
    const response = await api.generateWeb3AuthSignature({
      headers: {
        'Cache-Control': 'no-cache',
        csrf_token: csrfToken,
        Pragma: 'no-cache'
      }
    })
    const authConnectorInstance = authConnector({
      connectorSettings: {
        originData: { [origin]: response.data.signature }
      }
    })

    connectors.push(authConnectorInstance)
  }

  const web3AuthOptions: Web3AuthOptions = {
    clientId,
    connectors,
    web3AuthNetwork: network
  }

  const web3auth = new Web3AuthNoModal(web3AuthOptions)

  await web3auth.init()
  return web3auth
}

export const Web3Provider = ({ children, csrfToken }: Web3ProviderProps) => {
  const router = useRouter()
  const [loginProvider, setLoginProvider] = useState<LoginProvider | null>(null)
  const [loggedIn, setLoggedIn] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [web3auth, setWeb3auth] = useState<Web3AuthNoModal | null>(null)

  const pathname = usePathname()

  useEffect(() => {
    setError(null)
  }, [pathname])

  useEffect(() => {
    const init = async () => {
      try {
        const web3auth = await initWeb3Auth(csrfToken)
        setWeb3auth(web3auth)
        setReady(true)
        if (web3auth.connected) {
          setLoggedIn(true)
        }
      } catch (error) {}
    }

    if (!csrfToken) return
    init()
  }, [csrfToken])

  const authenticate = async (loginProvider: LoginProvider) => {
    setSubmitting(true)
    setError(null)
    setLoginProvider(loginProvider)
    try {
      if (loggedIn) await web3auth?.logout()
    } catch {}

    try {
      await web3auth?.connectTo(WALLET_CONNECTORS.AUTH, { authConnection: getAuthConnection(loginProvider) })
    } catch (e) {
      setError(getWeb3AuthErrorMessage(e))
      setSubmitting(false)
      setLoginProvider(null)
      return
    }

    if (web3auth?.connected) {
      setLoggedIn(true)
    }

    try {
      const userInfo = await web3auth?.getUserInfo()

      const response = await api.webThreeAuthAuthenticatePost(
        `Bearer ${userInfo?.idToken}`,
        {
          newsletter: false
        },
        {
          headers: {
            'Cache-Control': 'no-cache',
            csrf_token: csrfToken,
            Pragma: 'no-cache'
            // recaptcha_bypass: query.bypass,
          }
        }
      )

      if (needsMfa(response.data.data, Number(response.data.session?.mfa_verified))) {
        return router.push('/login/verify')
      }

      const nextHref = response?.data?.session?.redirect_to ?? getRedirecURL()
      if (window.location.pathname !== nextHref) {
        window.location.href = nextHref
      }
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setLoginProvider(null)
      setSubmitting(false)
    }
  }

  const logout = async () => {
    await web3auth?.logout()
    setLoginProvider(null)
    setLoggedIn(false)
  }

  return (
    <Web3Contenxt.Provider value={{ authenticate, error, loggedIn, loginProvider, logout, ready, submitting }}>
      {children}
    </Web3Contenxt.Provider>
  )
}

export const useWeb3Auth = () => {
  const context = useContext(Web3Contenxt)
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider')
  }
  return context
}
